import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import useShortlistStore from 'Store/useShortlistStore';

/**
 * useWorkforcePlanStore
 *
 * Session-only draft of a workforce growth plan — Workforce Planning's
 * mirror of useRedeploymentPlanStore, keyed by the DEMAND role's
 * jd_sb_unique_id (the role being grown, not anyone's current role):
 *
 *   - the Demand step saves each role's raised Target Positions
 *     (setTargetPosition);
 *   - Candidate Profiles entered from the Candidates step (?flow=workforce)
 *     shortlist store-only — no POST /talent-mobility/shortlist — recording
 *     the pair here (addShortlistedEmployee);
 *   - Submit sends the whole draft in one call (getWorkforcePlanPayload),
 *     then reset() clears it. LayoutV2 also calls reset() when navigation
 *     leaves the flow (see isWorkforceFlowLocation).
 *
 * planId/planName (setPlan) track which saved plan this draft belongs to,
 * once it's been saved at least once — see useWorkforcePlanActions for how
 * the header buttons use them to switch between creating a new plan and
 * updating the same one, mirroring useRedeploymentPlanStore exactly.
 *
 * getWorkforcePlanPayload assembles the bulk submit payload:
 *
 *   {
 *     plan_name: "...",
 *     shortlisted_employee_targets: { "<demand_role_jd>": ["<user_id>"] },
 *     target_positions: { "<demand_role_jd>": 3 },
 *     notifications: {
 *       "<demand_role_jd>": {
 *         "<user_id>": { employee: false, line_manager: false, hr_manager: false },
 *       },
 *     },
 *   }
 *
 * notifications records the Shortlist dialog's notify checkboxes (Notify
 * Employee / Line Manager / Hiring Manager) per shortlisted employee — the
 * backend stores each demand role's map on its workforce_user_plan row's
 * notifications JSON column.
 *
 * Deliberately NOT persisted to localStorage — same rule as the other
 * Talent Mobility stores.
 */
// One flag per notify checkbox on the Shortlist dialog — all off when a
// shortlist path didn't ask.
const DEFAULT_NOTIFICATIONS = { employee: false, line_manager: false, hr_manager: false };

const useWorkforcePlanStore = create(
  devtools(
    (set, get) => ({
      // demandRoleJdId -> target position edited in the Demand step
      targetPositions: {},

      // demandRoleJdId -> [employee_unique_id, ...] shortlisted for it
      shortlistedEmployeeTargets: {},

      // demandRoleJdId -> { employee_unique_id: { employee, line_manager, hr_manager } }
      shortlistNotifications: {},

      // plan_id / plan_name of the saved plan this draft is currently
      // attached to — null/"" for a brand-new, never-saved draft (header
      // shows "New Plan"). Cleared by reset(), same as everything else the
      // draft holds. See useRedeploymentPlanStore's identical fields.
      planId: null,
      planName: "",

      setPlan: (planId, planName) => set({ planId, planName }),

      // Replaces the whole draft with one saved plan's stored data — the
      // View Planning "Edit" action's counterpart to getWorkforcePlanPayload,
      // going the other direction: workforcePlans is the
      // POST /workforce-plan/details response's per-role array
      // ({ jd_sb_unique_id, shortlisted_users, target_position,
      // notifications }), unpacked back into the three maps this store keys
      // by JD. reset() first so a leftover unsaved draft's shortlist tags
      // don't bleed into the plan being loaded, then every shortlisted
      // employee in the loaded plan is re-seeded into useShortlistStore —
      // ShortlistTag (the Candidates step's CandidateRow) reads purely from
      // that store, so without this a genuinely shortlisted employee would
      // silently show as not shortlisted right after loading. Mirrors
      // useRedeploymentPlanStore.loadPlan.
      loadPlan: ({ planId, planName, workforcePlans }) => {
        get().reset();

        const targetPositions = {};
        const shortlistedEmployeeTargets = {};
        const shortlistNotifications = {};
        const shortlistCandidate = useShortlistStore.getState().shortlistCandidate;
        (workforcePlans || []).forEach((role) => {
          const jdId = role.jd_sb_unique_id;
          if (!jdId) return;
          targetPositions[jdId] = role.target_position ?? null;
          const employeeIds = Array.isArray(role.shortlisted_users) ? role.shortlisted_users : [];
          shortlistedEmployeeTargets[jdId] = employeeIds;
          const roleNotifications = role.notifications || {};
          const jdNotifications = Object.fromEntries(
            employeeIds.map((employeeId) => [
              employeeId,
              roleNotifications[employeeId] || { ...DEFAULT_NOTIFICATIONS },
            ])
          );
          shortlistNotifications[jdId] = jdNotifications;
          employeeIds.forEach((employeeId) => {
            const notify = jdNotifications[employeeId];
            shortlistCandidate(jdId, employeeId, notify.employee, notify.line_manager, notify.hr_manager);
          });
        });

        set({ targetPositions, shortlistedEmployeeTargets, shortlistNotifications, planId, planName });
      },

      setTargetPosition: (demandRoleJdId, targetPosition) =>
        set((state) => {
          const targetPositions = { ...state.targetPositions, [demandRoleJdId]: targetPosition };
          return { targetPositions };
        }),

      addShortlistedEmployee: (demandRoleJdId, employeeId, notifications) =>
        set((state) => {
          const employeeIds = state.shortlistedEmployeeTargets[demandRoleJdId] ?? [];
          const roleNotifications = {
            ...state.shortlistNotifications[demandRoleJdId],
            [employeeId]:
              notifications ||
              state.shortlistNotifications[demandRoleJdId]?.[employeeId] ||
              { ...DEFAULT_NOTIFICATIONS },
          };
          const shortlistNotifications = {
            ...state.shortlistNotifications,
            [demandRoleJdId]: roleNotifications,
          };
          if (employeeIds.includes(employeeId)) {
            // Already shortlisted — a re-confirm only refreshes the flags.
            return { shortlistNotifications };
          }
          const shortlistedEmployeeTargets = {
            ...state.shortlistedEmployeeTargets,
            [demandRoleJdId]: [...employeeIds, employeeId],
          };
          return { shortlistedEmployeeTargets, shortlistNotifications };
        }),

      // Undo Shortlist's store-only counterpart to addShortlistedEmployee —
      // drops the employee from the demand role's draft (and its
      // notifications entry) without touching any other role's targets.
      removeShortlistedEmployee: (demandRoleJdId, employeeId) =>
        set((state) => {
          const employeeIds = state.shortlistedEmployeeTargets[demandRoleJdId] ?? [];
          if (!employeeIds.includes(employeeId)) return state;
          const shortlistedEmployeeTargets = {
            ...state.shortlistedEmployeeTargets,
            [demandRoleJdId]: employeeIds.filter((id) => id !== employeeId),
          };
          const roleNotifications = { ...state.shortlistNotifications[demandRoleJdId] };
          delete roleNotifications[employeeId];
          const shortlistNotifications = {
            ...state.shortlistNotifications,
            [demandRoleJdId]: roleNotifications,
          };
          return { shortlistedEmployeeTargets, shortlistNotifications };
        }),

      getWorkforcePlanPayload: (planName) => {
        const { targetPositions, shortlistedEmployeeTargets, shortlistNotifications } = get();
        const activeTargets = Object.entries(shortlistedEmployeeTargets).filter(
          ([, employeeIds]) => employeeIds.length
        );
        const payload = {
          plan_name: planName,
          shortlisted_employee_targets: Object.fromEntries(activeTargets),
          target_positions: { ...targetPositions },
          // Per demand role, each shortlisted employee's notify flags —
          // exactly the map the backend stores on the row's notifications
          // JSON column.
          notifications: Object.fromEntries(
            activeTargets.map(([demandRoleJdId, employeeIds]) => [
              demandRoleJdId,
              Object.fromEntries(
                employeeIds.map((employeeId) => [
                  employeeId,
                  shortlistNotifications[demandRoleJdId]?.[employeeId] || { ...DEFAULT_NOTIFICATIONS },
                ])
              ),
            ])
          ),
        };
        return payload;
      },

      reset: () => {
        // Shortlists made inside the flow also live in useShortlistStore (so
        // ShortlistTag shows them on every table) — take those pairs back
        // too, same as useRedeploymentPlanStore.reset().
        const { shortlistedEmployeeTargets } = get();
        const removeShortlist = useShortlistStore.getState().removeShortlist;
        Object.entries(shortlistedEmployeeTargets).forEach(([demandRoleJdId, employeeIds]) =>
          employeeIds.forEach((employeeId) => removeShortlist(demandRoleJdId, employeeId))
        );
        set({
          targetPositions: {},
          shortlistedEmployeeTargets: {},
          shortlistNotifications: {},
          planId: null,
          planName: "",
        });
      },
    }),
    { name: 'WorkforcePlanStore' }
  )
);

/**
 * Whether a router location is inside the workforce-planning flow: the
 * Workforce Planning page itself, or a Candidate Profile entered from its
 * Candidates step (those carry ?flow=workforce). LayoutV2 watches route
 * changes and resets this store as soon as navigation leaves the flow.
 */
export const isWorkforceFlowLocation = ({ pathname, search }) =>
  pathname.startsWith('/talent-mobility/workforce-planning') ||
  (pathname.startsWith('/talent-mobility/candidate/') && new URLSearchParams(search).get('flow') === 'workforce');

export default useWorkforcePlanStore;
